import { useQuery } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { voiceSelectionService } from '../services/voiceSelectApi';
import VoiceCard from '../components/GetStarted/VoiceCard';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { setCharacterVoice } from 'store/slices/podcastGen';
import VoiceSelectionSkeleton from '../components/Skeletons/VoiceSelection';
import { RootState } from 'store';
import { CharacterErrors } from '../components/GetStarted/HostForm';

interface VoiceSelectionProps {
  setVoiceDialog: (value: boolean) => void;
  index: number;
  role: 'host' | 'guest';
  errors: CharacterErrors;
  setErrors: (errors: CharacterErrors) => void;
}

export function VoiceSelection({
  setVoiceDialog,
  index,
  role,
  errors,
  setErrors,
}: VoiceSelectionProps) {
  const { hosts, guests } = useSelector((state: RootState) => state.podcastGen);

  const { data: voices = [], isLoading } = useQuery({
    queryKey: ['Voices'],
    queryFn: voiceSelectionService.getVoices,
  });
  const [selected, setSelected] = useState<{ name: string; gender: 'M' | 'F' }>(
    {
      name: '',
      gender: 'M',
    }
  );
  const [tab, setTab] = useState<'Female' | 'Male'>('Female');
  const dispatch = useDispatch();

  const handleConfirm = () => {
    if (!selected.name) {
      setErrors({ ...errors, voice: 'Voice selection is required' });
      return;
    }
    
    setErrors({ ...errors, voice: undefined });
    
    dispatch(
      setCharacterVoice({
        index,
        voice_name: selected.name,
        gender: selected.gender,
        role: role,
      })
    );
    setVoiceDialog(false);
  };

  if (isLoading) {
    return <VoiceSelectionSkeleton />;
  }

  return (
    <div className="w-full max-w-[850px] rounded-md text-text-primary h-auto md:h-[410px] bg-background-overlay/50">
      <div className="flex flex-col md:flex-row">
        {/* Voices Selection */}
        <div className="flex flex-col gap-4 justify-center p-8 sm:pl-10 bg-background sm:w-[650px] rounded-t-md sm:rounded-l-md sm:rounded-r-none">
          {/* Tab Selection for Mobile */}
          <div className="md:hidden flex justify-around">
            <button
              className={`text-[16px] ${tab === 'Female' ? 'font-bold border-b-2 border-primary' : ' border-b-2 border-transparent'}`}
              onClick={() => setTab('Female')}
            >
              Female
            </button>
            <button
              className={`text-[16px] ${tab === 'Male' ? 'font-bold border-b-2 border-primary' : ' border-b-2 border-transparent'}`}
              onClick={() => setTab('Male')}
            >
              Male
            </button>
          </div>

          <div className="grid grid-flow-col md:grid-cols-2 gap-x-10 ">
            {/* Female Voices */}
            {(tab === 'Female' || window.innerWidth >= 768) && (
              <div className="w-full">
                <h1 className=" hidden sm:flex uppercase text-[16px] font-normal">
                  Female
                </h1>
                <div className="flex flex-col gap-4 mt-0 sm:mt-4 overflow-y-auto overflow-x-hidden max-h-[300px] shadow-inner rounded-md">
                  {voices
                    .filter(
                      (voice) =>
                        voice.gender === 'Female' &&
                        ![...hosts, ...guests].some(
                          (character) => character.voice_name === voice.name
                        )
                    )
                    .map((voice) => (
                      <VoiceCard
                        key={voice.id}
                        name={voice.name}
                        img={voice.url}
                        preview={voice.voice}
                        gender="F"
                        selected={selected}
                        setSelected={setSelected}
                      />
                    ))}
                </div>
              </div>
            )}
            {/* Male Voices */}
            {(tab === 'Male' || window.innerWidth >= 768) && (
              <div className="w-full">
                <h1 className=" uppercase text-[16px] font-normal hidden sm:flex">
                  Male
                </h1>
                <div className="flex flex-col gap-4 mt-0 sm:mt-4 overflow-y-auto overflow-x-hidden max-h-[300px] h-full shadow-inner rounded-md">
                  {voices
                    .filter(
                      (voice) =>
                        voice.gender === 'Male' &&
                        ![...hosts, ...guests].some(
                          (character) => character.voice_name === voice.name
                        )
                    )
                    .map((voice) => (
                      <VoiceCard
                        key={voice.id}
                        name={voice.name}
                        img={voice.url}
                        preview={voice.voice}
                        gender="M"
                        selected={selected}
                        setSelected={setSelected}
                      />
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="pt-1 flex w-full gap-4 justify-end">
            <Button
              variant="customSecondary"
              type="button"
              className="text-[14px] bg-secondary"
              size="sm"
              onClick={() => setVoiceDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="customSecondary"
              disabled={selected.name?.length === 0}
              type="button"
              className="text-[14px] bg-secondary "
              size="sm"
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </div>
        </div>

        {/* Image Section */}
        <div className="bg-background-card w-full md:w-[270px] rounded-br-md md:rounded-r-md flex flex-col items-center justify-center gap-4 p-4 py-12">
          <p className="text-left font-bold text-lg md:text-2xl">
            Celebrity voices coming soon. Stay tuned!
          </p>
          <div className="w-32 h-28 hidden md:block md:w-52 md:h-40 bg-white rounded-md">
            <img
              src="/podcast-voice-image.png"
              alt="podcast"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
